/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import { Result } from '@blade/std-lib'
import { reatomCurrencyInput } from '@root/features/currency/currency-input-reatom'
import { processSellSwapAction } from '@root/features/trade/swap'
import { tradeSettingsAtom } from '@root/features/trade/trade-settings'
import {
  InsufficientBalanceError,
  NoAmountError,
} from '@root/features/trade/validation-errors'
import { Error } from '@root/shared'
import { reatomRequest } from '@root/shared/lib'

import { reatomControlsQuote } from '../reatom-controls-quote'

import type { Atom } from '@reatom/framework'
import type { PairModel } from '@root/entities/pair'
import type { WalletModel } from '@root/entities/wallet'

export const reatomMarketSellControls = ({
  pairAtom,
  walletAtom,
}: {
  pairAtom: Atom<PairModel | undefined>
  walletAtom: Atom<WalletModel | undefined>
}) => {
  const quoteCurrencyAtom = atom((ctx) => {
    const pair = ctx.spy(pairAtom)
    return pair?.quote
  }, 'quoteAtom')

  const inputModel = reatomCurrencyInput(
    { currencyAtom: quoteCurrencyAtom, walletAtom },
    'inputModel',
  )

  const quoteModel = reatomControlsQuote({
    amountAtom: inputModel.amountAtom,
    direction: 'sell',
    name: 'tradingSellQuoteModel',
  })

  const validationAtom = atom(
    (ctx): InsufficientBalanceError | NoAmountError | undefined => {
      const amount = ctx.spy(inputModel.amountAtom)
      const balance = ctx.spy(inputModel.balanceAtom)
      const currencyIn = ctx.get(quoteCurrencyAtom)

      if (amount.gt(balance)) {
        return new InsufficientBalanceError({
          amount,
          balance,
          currency: currencyIn,
        })
      }
      if (amount.isZero()) {
        return new NoAmountError()
      }
      return undefined
    },
    'validationAtom',
  )

  const processSellAction = action(async (ctx) => {
    const amountIn = ctx.get(inputModel.amountAtom)
    const settings = ctx.get(tradeSettingsAtom)
    const pool = ctx.get(pairAtom)
    const wallet = ctx.get(walletAtom)
    const quote = ctx.get(quoteModel.quoteStateAtom).value

    if (!pool || amountIn.eq(0) || !wallet) {
      return Result.fail(Error.UnknownError.fromUnknown('invalid input'))
    }

    return processSellSwapAction(ctx, {
      amount: amountIn,
      pair: pool,
      wallet: wallet,
      settings: settings,
      quote,
    })
  }, 'processSellAction')

  const executeSwapModel = reatomRequest({
    execAction: processSellAction,
    name: `executeSwapModel`,
    concurrently: false,
  })

  const receiveAmountNativeAtom = atom((ctx) => {
    const pair = ctx.spy(pairAtom)
    if (!pair) {
      return BigNumber(0)
    }
    const priceNative = ctx.spy(pair.priceRealtime).priceNative
    if (priceNative.isZero()) {
      return BigNumber(0)
    }

    const amountCurrency = ctx.spy(inputModel.amountAtom)
    const amountNative = amountCurrency.multipliedBy(priceNative)
    // TODO: Calculate considering fees
    return amountNative
  }, 'receiveAmountNativeAtom')

  return {
    executeSwapModel: executeSwapModel,
    processSellAction,
    validationAtom,
    inputModel,
    receiveAmountNativeAtom,
    quoteModel,
  }
}

export type MarketSellControlsModel = ReturnType<
  typeof reatomMarketSellControls
>
